import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';
import type { IsDraft } from '@sysarb/sysarb-types-v2/services/v1/job';
import type { ExtendedExternalJobV2, JobProfileKeyValuesV2 } from '@sysarb/sysarb-types-v2/services/v1/job-architecture';
import type { JobKeyValuesV4Input } from '@sysarb/sysarb-types-v2/services/v1/job/payRange';
import { keepPreviousData, queryOptions } from '@tanstack/vue-query';
import type { QueryOptions } from '@tanstack/vue-query';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);

type JobKeyValuesOptions = {
    queries: {
        getJobKeyValues: (
            jobId: string | null,
            payload: IsDraft & { salaryType?: EmploymentSalaryType }
        ) => QueryOptions<JobProfileKeyValuesV2[] | null, Error>;
        getJobKeyValuesV4: (
            jobId: string | null,
            payload: JobKeyValuesV4Input & { salaryType?: EmploymentSalaryType }
        ) => QueryOptions<JobProfileKeyValuesV2[] | null, Error>;
        getExternalJobKeyValues: (
            jobId: string | null,
            payload?: { salaryType?: EmploymentSalaryType; payRangeId?: string }
        ) => QueryOptions<ExtendedExternalJobV2 | null, Error>;
    };
};

export const jobKeyValues: JobKeyValuesOptions = {
    queries: {
        getJobKeyValues: (
            jobId: string | null,
            payload: IsDraft & { salaryType?: EmploymentSalaryType }
        ): QueryOptions<JobProfileKeyValuesV2[] | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const { salaryType, ...isDraft } = payload;
            const query = salaryType != null ? `?salaryType=${salaryType}` : '';
            const topic = `v1.${instanceTopic}.job.${jobId}${query}`;
            return queryOptions<JobProfileKeyValuesV2[] | null, Error>({
                queryKey: ['job-key-values', topic, isDraft],
                queryFn: async (): Promise<JobProfileKeyValuesV2[] | null> => {
                    return ResgateManager.getInstance().call<JobProfileKeyValuesV2[]>(topic, 'getkeyvalues', isDraft).then(res => {
                        if (res instanceof ResErr) throw new Error(res.message);
                        return res;
                    });
                },
                enabled: () => jobId != null,
                placeholderData: keepPreviousData,
            });
        },

        getJobKeyValuesV4: (
            jobId: string | null,
            payload: JobKeyValuesV4Input & { salaryType?: EmploymentSalaryType }
        ): QueryOptions<JobProfileKeyValuesV2[] | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const { salaryType, ...input } = payload;
            const query = salaryType != null ? `?salaryType=${salaryType}` : '';
            const topic = `v1.${instanceTopic}.job.${jobId}${query}`;
            return queryOptions<JobProfileKeyValuesV2[] | null, Error>({
                queryKey: ['job-key-values-v4', topic, input],
                queryFn: async (): Promise<JobProfileKeyValuesV2[] | null> => {
                    return ResgateManager.getInstance().call<JobProfileKeyValuesV2[]>(topic, 'getkeyvaluesv4', input).then(res => {
                        if (res instanceof ResErr) throw new Error(res.message);
                        return res;
                    });
                },
                enabled: () => jobId != null,
                placeholderData: keepPreviousData,
            });
        },
        /**
         * Key values for an external job profile. When a `payRangeId` is passed the numbers are
         * limited to the employments assigned to that external pay range node.
         */
        getExternalJobKeyValues: (
            jobId: string | null,
            payload?: { salaryType?: EmploymentSalaryType; payRangeId?: string }
        ): QueryOptions<ExtendedExternalJobV2 | null, Error> => {
            const { instance } = useLoginStore();
            const urlEncodedId = jobId != null ? encodeURIComponent(jobId) : '';
            const params = new URLSearchParams();
            if (payload?.salaryType != null) params.set('salaryType', String(payload.salaryType));
            if (payload?.payRangeId != null) params.set('payRangeId', payload.payRangeId);
            const queryString = params.toString();
            const query = queryString.length > 0 ? `?${queryString}` : '';
            const url = `v4/${instance}/external/job/${urlEncodedId}${query}`;
            return queryOptions<ExtendedExternalJobV2 | null, Error>({
                queryKey: ['external-job-key-values', url],
                queryFn: async (): Promise<ExtendedExternalJobV2 | null> => {
                    return jobService.get<ExtendedExternalJobV2 | null>(url);
                },
                enabled: () => jobId != null,
                placeholderData: keepPreviousData,
            });
        },
    },
};
